import React, { useEffect, useRef, useState } from 'react';

// Mínimo de caracteres antes de consultar o AD (reduz consumo de recurso).
const MIN_QUERY = 3;

/**
 * Campo de "adicionar usuário" com autocomplete do AD (CR-5 / Ajuste 4).
 * Ao digitar (nome ou e-mail), consulta /api/searchUsers e sugere correspondências.
 * Mantém compatibilidade com o uso antigo: `value` é o texto/e-mail atual e
 * `onChange(text)` o atualiza no pai (que valida "@" antes de adicionar).
 */
function UserSearchInput({ value, onChange, placeholder, disabled }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [justPicked, setJustPicked] = useState(false);
  const boxRef = useRef(null);

  // Busca com debounce; não reabre logo após escolher uma sugestão.
  useEffect(() => {
    const q = (value || '').trim();
    if (justPicked) { setJustPicked(false); return; }
    // Mínimo de 3 caracteres p/ economizar chamadas ao Graph. Ao apagar o texto
    // (query curta), encerra também o loading — senão o spinner ficava girando.
    if (q.length < MIN_QUERY) { setResults([]); setOpen(false); setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/api/searchUsers?q=${encodeURIComponent(q)}`);
        const data = await res.json();
        if (cancelled) return;
        const users = Array.isArray(data.users) ? data.users : [];
        setResults(users);
        setOpen(users.length > 0);
        setHighlight(-1);
      } catch {
        if (!cancelled) { setResults([]); setOpen(false); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 260);

    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Fecha ao clicar fora
  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = (user) => {
    setJustPicked(true);
    onChange(user.email);
    setResults([]);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault();
      pick(results[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="user-search" ref={boxRef}>
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder || 'Nome ou e-mail…'}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul className="user-search-list">
          {results.map((u, i) => (
            <li
              key={u.email || i}
              className={`user-search-item${i === highlight ? ' is-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              onMouseEnter={() => setHighlight(i)}
            >
              <span className="user-search-name">{u.displayName}</span>
              <span className="user-search-mail">{u.email}</span>
              {(u.jobTitle || u.officeLocation) && (
                <span className="user-search-meta">
                  {[u.jobTitle, u.officeLocation].filter(Boolean).join(' • ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {loading && <span className="user-search-spinner" aria-hidden="true" />}
    </div>
  );
}

export default UserSearchInput;
